import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Pencil, Check, X, Trash2, Wand2, ChevronRight, Link2, Bug } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';

const API_URL = process.env.REACT_APP_BACKEND_URL;
function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

function TeamBadge({ teamId, size = 28 }) {
    if (!teamId) return null;
    return (
        <img
            src={badgeUrl(teamId)}
            alt="" width={size} height={size}
            className="rounded-sm object-contain flex-shrink-0"
            onError={e => { e.target.style.display = 'none'; }}
        />
    );
}

export default function AdminStatsApiMappingsPage() {
    const [competitions, setCompetitions] = useState([]);
    const [selectedComp, setSelectedComp]   = useState('comp_3039'); // PL default
    const [statsTeams, setStatsTeams]       = useState([]);
    const [paTeams, setPaTeams]             = useState([]);
    const [mappings, setMappings]           = useState({});   // statsapi_team_id → mapping doc
    const [paMappings, setPaMappings]       = useState({});   // pa_team_id → mapping doc
    const [loading, setLoading]             = useState(true);
    const [loadingTeams, setLoadingTeams]   = useState(false);

    // Edit dialog
    const [editTeam, setEditTeam]         = useState(null);
    const [paSearch, setPaSearch]         = useState('');
    const [saving, setSaving]             = useState(false);

    // Load PA teams from a specific competition
    const [paCompId, setPaCompId]         = useState('');
    const [loadingFromPA, setLoadingFromPA] = useState(false);

    // Auto-match
    const [autoMatching, setAutoMatching]   = useState(false);
    const [suggestions, setSuggestions]     = useState(null); // null = not run yet
    const [selected, setSelected]           = useState({});   // statsapi_team_id → true/false
    const [applying, setApplying]           = useState(false);

    // Search
    const [search, setSearch] = useState('');

    // Match mapping panel
    const [matchPaId, setMatchPaId]           = useState('');
    const [matchStatsApiId, setMatchStatsApiId] = useState('');
    const [matchMapping, setMatchMapping]     = useState(null);  // result from GET
    const [matchChecking, setMatchChecking]   = useState(false);
    const [matchSaving, setMatchSaving]       = useState(false);
    const [matchDebugData, setMatchDebugData] = useState(null);
    const [matchDebugging, setMatchDebugging] = useState(false);

    // Load competitions + PA teams + existing mappings
    useEffect(() => {
        Promise.all([
            axios.get(`${API_URL}/api/admin/statsapi/competitions`, { headers: authHeaders() }),
            axios.get(`${API_URL}/api/admin/statsapi/pa-teams`,     { headers: authHeaders() }),
            axios.get(`${API_URL}/api/admin/statsapi/mappings`,     { headers: authHeaders() }),
        ]).then(([compsRes, paRes, mapsRes]) => {
            setCompetitions(compsRes.data.competitions || []);
            setPaTeams(paRes.data.teams || []);
            const byStatsApi = {};
            const byPa = {};
            for (const m of (mapsRes.data.mappings || [])) {
                byStatsApi[m.statsapi_team_id] = m;
                byPa[m.pa_team_id] = m;
            }
            setMappings(byStatsApi);
            setPaMappings(byPa);
        }).catch(() => toast.error('Failed to load mapping data'))
          .finally(() => setLoading(false));
    }, []);

    // Load statsapi teams when competition changes
    useEffect(() => {
        if (!selectedComp) return;
        setLoadingTeams(true);
        axios.get(`${API_URL}/api/admin/statsapi/teams?competition_id=${selectedComp}`, { headers: authHeaders() })
            .then(r => setStatsTeams(r.data.teams || []))
            .catch(() => toast.error('Failed to load teams'))
            .finally(() => setLoadingTeams(false));
    }, [selectedComp]);

    const refreshMappings = useCallback(async () => {
        const r = await axios.get(`${API_URL}/api/admin/statsapi/mappings`, { headers: authHeaders() });
        const byStatsApi = {};
        const byPa = {};
        for (const m of (r.data.mappings || [])) {
            byStatsApi[m.statsapi_team_id] = m;
            byPa[m.pa_team_id] = m;
        }
        setMappings(byStatsApi);
        setPaMappings(byPa);
    }, []);

    async function loadPaTeamsFromComp() {
        if (!paCompId.trim()) return;
        setLoadingFromPA(true);
        try {
            const r = await axios.get(`${API_URL}/api/admin/statsapi/pa-teams-from-competition?pa_comp_id=${paCompId.trim()}`, { headers: authHeaders() });
            const fetched = r.data.teams || [];
            // Merge into existing paTeams list (deduplicate by pa_team_id)
            setPaTeams(prev => {
                const map = {};
                [...prev, ...fetched].forEach(t => { map[t.pa_team_id] = t; });
                return Object.values(map).sort((a, b) => a.pa_team_name.localeCompare(b.pa_team_name));
            });
            toast.success(`Loaded ${fetched.length} teams from PA competition ${paCompId}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load teams from PA');
        } finally {
            setLoadingFromPA(false);
        }
    }

    async function saveMapping(paTeam) {
        setSaving(true);
        try {
            await axios.post(`${API_URL}/api/admin/statsapi/mappings`, {
                pa_team_id:         paTeam.pa_team_id,
                pa_team_name:       paTeam.pa_team_name,
                statsapi_team_id:   editTeam.id,
                statsapi_team_name: editTeam.name,
                competition_id:     selectedComp,
            }, { headers: authHeaders() });
            toast.success(`Mapped ${editTeam.name} → ${paTeam.pa_team_name}`);
            setEditTeam(null);
            await refreshMappings();
        } catch {
            toast.error('Failed to save mapping');
        } finally {
            setSaving(false);
        }
    }

    async function runAutoMatch() {
        setAutoMatching(true);
        setSuggestions(null);
        try {
            const r = await axios.get(`${API_URL}/api/admin/statsapi/auto-match?competition_id=${selectedComp}`, { headers: authHeaders() });
            const suggs = r.data.suggestions || [];
            setSuggestions(suggs);
            // Pre-select high + medium confidence
            const pre = {};
            suggs.forEach(s => { if (s.band !== 'low') pre[s.statsapi_team_id] = true; });
            setSelected(pre);
        } catch {
            toast.error('Auto-match failed');
        } finally {
            setAutoMatching(false);
        }
    }

    async function applySelected() {
        const toApply = (suggestions || []).filter(s => selected[s.statsapi_team_id]);
        if (!toApply.length) { toast.error('No suggestions selected'); return; }
        setApplying(true);
        let saved = 0, failed = 0;
        for (const s of toApply) {
            try {
                await axios.post(`${API_URL}/api/admin/statsapi/mappings`, {
                    pa_team_id:         s.pa_team_id,
                    pa_team_name:       s.pa_team_name,
                    statsapi_team_id:   s.statsapi_team_id,
                    statsapi_team_name: s.statsapi_team_name,
                    competition_id:     selectedComp,
                }, { headers: authHeaders() });
                saved++;
            } catch { failed++; }
        }
        setApplying(false);
        setSuggestions(null);
        setSelected({});
        await refreshMappings();
        toast.success(`Applied ${saved} mapping${saved !== 1 ? 's' : ''}${failed ? ` (${failed} failed)` : ''}`);
    }

    async function deleteMapping(statsapiTeamId) {
        const m = mappings[statsapiTeamId];
        if (!m) return;
        try {
            await axios.delete(`${API_URL}/api/admin/statsapi/mappings/${m.pa_team_id}`, { headers: authHeaders() });
            toast.success('Mapping removed');
            await refreshMappings();
        } catch {
            toast.error('Failed to remove mapping');
        }
    }

    async function checkMatchMapping() {
        if (!matchPaId.trim()) return;
        setMatchChecking(true);
        setMatchMapping(null);
        setMatchDebugData(null);
        try {
            const r = await axios.get(`${API_URL}/api/admin/statsapi/match-mapping/${matchPaId.trim()}`, { headers: authHeaders() });
            setMatchMapping(r.data);
            if (r.data.statsapi_match_id) setMatchStatsApiId(r.data.statsapi_match_id);
        } catch {
            toast.error('Failed to check match mapping');
        } finally {
            setMatchChecking(false);
        }
    }

    async function saveMatchMapping() {
        if (!matchPaId.trim() || !matchStatsApiId.trim()) return;
        setMatchSaving(true);
        try {
            await axios.post(`${API_URL}/api/admin/statsapi/match-mapping`, {
                pa_match_id:       matchPaId.trim(),
                statsapi_match_id: matchStatsApiId.trim(),
            }, { headers: authHeaders() });
            toast.success(`Mapped PA ${matchPaId.trim()} → ${matchStatsApiId.trim()}`);
            // Refresh the display
            const r = await axios.get(`${API_URL}/api/admin/statsapi/match-mapping/${matchPaId.trim()}`, { headers: authHeaders() });
            setMatchMapping(r.data);
        } catch {
            toast.error('Failed to save match mapping');
        } finally {
            setMatchSaving(false);
        }
    }

    async function deleteMatchMapping() {
        if (!matchPaId.trim()) return;
        try {
            await axios.delete(`${API_URL}/api/admin/statsapi/match-mapping/${matchPaId.trim()}`, { headers: authHeaders() });
            toast.success('Match mapping cleared');
            setMatchMapping({ pa_match_id: matchPaId.trim(), statsapi_match_id: null });
            setMatchStatsApiId('');
        } catch {
            toast.error('Failed to clear match mapping');
        }
    }

    async function debugMatchMapping() {
        if (!matchPaId.trim()) return;
        setMatchDebugging(true);
        setMatchDebugData(null);
        try {
            const r = await axios.get(`${API_URL}/api/admin/statsapi/debug/${matchPaId.trim()}`, { headers: authHeaders() });
            setMatchDebugData(r.data);
        } catch (e) {
            setMatchDebugData(e?.response?.data || { error: 'Request failed' });
        } finally {
            setMatchDebugging(false);
        }
    }

    const filtered = statsTeams.filter(t =>
        !search || t.name.toLowerCase().includes(search.toLowerCase())
    );
    const mapped   = filtered.filter(t => mappings[t.id]);
    const unmapped = filtered.filter(t => !mappings[t.id]);

    const filteredPa = paTeams.filter(t =>
        !paSearch || t.pa_team_name.toLowerCase().includes(paSearch.toLowerCase())
    );

    const totalMapped = statsTeams.filter(t => mappings[t.id]).length;

    return (
        <div className="container max-w-3xl mx-auto py-6 px-4 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/admin/football">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl font-bold page-title">Shot Map Team Mappings</h1>
                    <p className="text-sm text-muted-foreground">
                        Link theStatsAPI teams to PA team IDs so shot maps can load automatically
                    </p>
                </div>
            </div>

            {/* Competition picker + summary + auto-match */}
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={selectedComp} onValueChange={setSelectedComp}>
                    <SelectTrigger className="w-64">
                        <SelectValue placeholder="Select competition" />
                    </SelectTrigger>
                    <SelectContent>
                        {competitions.filter(c => c.xg_available).map(c => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name}{c.country ? ` (${c.country})` : ''}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {!loadingTeams && statsTeams.length > 0 && (
                    <div className="flex gap-3 items-center">
                        <div className="rounded-lg border bg-card px-3 py-1.5 text-center min-w-[60px]">
                            <p className="text-lg font-bold text-emerald-400">{totalMapped}</p>
                            <p className="text-[10px] text-muted-foreground">Mapped</p>
                        </div>
                        <div className="rounded-lg border bg-card px-3 py-1.5 text-center min-w-[60px]">
                            <p className="text-lg font-bold text-red-400">{statsTeams.length - totalMapped}</p>
                            <p className="text-[10px] text-muted-foreground">Unmapped</p>
                        </div>
                        {statsTeams.length - totalMapped > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-2 border-primary/40 text-primary hover:bg-primary/10"
                                onClick={runAutoMatch}
                                disabled={autoMatching}
                            >
                                <Wand2 className={`h-3.5 w-3.5 ${autoMatching ? 'animate-spin' : ''}`} />
                                {autoMatching ? 'Matching…' : 'Auto-match'}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Auto-match suggestions panel */}
            {suggestions && (
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-semibold text-sm">Auto-match suggestions</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Review and confirm. Green = high confidence, amber = check before saving, red = low confidence.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setSuggestions(null)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {suggestions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-3">All teams already mapped for this competition.</p>
                    ) : (
                        <>
                            {/* Select all / none */}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <button className="hover:text-foreground underline" onClick={() => {
                                    const all = {};
                                    suggestions.forEach(s => { all[s.statsapi_team_id] = true; });
                                    setSelected(all);
                                }}>Select all</button>
                                <button className="hover:text-foreground underline" onClick={() => setSelected({})}>None</button>
                                <span className="ml-auto">{Object.values(selected).filter(Boolean).length} of {suggestions.length} selected</span>
                            </div>

                            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                                {suggestions.map(s => {
                                    const isSelected = !!selected[s.statsapi_team_id];
                                    const bandColor = s.band === 'high' ? 'border-emerald-500/30 bg-emerald-950/20'
                                        : s.band === 'medium' ? 'border-amber-500/30 bg-amber-950/20'
                                        : 'border-red-500/30 bg-red-950/20';
                                    const pctColor = s.band === 'high' ? 'text-emerald-400'
                                        : s.band === 'medium' ? 'text-amber-400'
                                        : 'text-red-400';
                                    return (
                                        <button
                                            key={s.statsapi_team_id}
                                            onClick={() => setSelected(prev => ({ ...prev, [s.statsapi_team_id]: !prev[s.statsapi_team_id] }))}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-all ${bandColor} ${isSelected ? 'ring-1 ring-primary/40' : 'opacity-60'}`}
                                        >
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                                                {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                                            </div>
                                            <span className="text-sm font-medium w-36 shrink-0 truncate">{s.statsapi_team_name}</span>
                                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                            <TeamBadge teamId={s.pa_team_id} size={20} />
                                            <span className="text-sm flex-1 truncate">{s.pa_team_name}</span>
                                            <span className={`text-xs font-mono font-bold shrink-0 ${pctColor}`}>
                                                {Math.round(s.confidence * 100)}%
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <Button
                                className="w-full"
                                onClick={applySelected}
                                disabled={applying || Object.values(selected).filter(Boolean).length === 0}
                            >
                                {applying ? 'Saving…' : `Apply ${Object.values(selected).filter(Boolean).length} mapping${Object.values(selected).filter(Boolean).length !== 1 ? 's' : ''}`}
                            </Button>
                        </>
                    )}
                </div>
            )}

            {/* Match ID Mapping panel */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-primary" />
                    <h2 className="font-semibold text-sm">Match ID Mapping</h2>
                    <span className="text-xs text-muted-foreground ml-1">Manually pin a PA match to a theStatsAPI match</span>
                </div>

                {/* PA Match ID row */}
                <div className="flex gap-2 items-center">
                    <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground mb-1">PA Match ID</p>
                        <Input
                            placeholder="e.g. 4546921"
                            value={matchPaId}
                            onChange={e => { setMatchPaId(e.target.value); setMatchMapping(null); setMatchDebugData(null); }}
                            onKeyDown={e => e.key === 'Enter' && checkMatchMapping()}
                            className="h-8 text-sm font-mono"
                        />
                    </div>
                    <div className="flex gap-1.5 self-end">
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1"
                            onClick={checkMatchMapping}
                            disabled={matchChecking || !matchPaId.trim()}
                        >
                            {matchChecking ? <span className="animate-spin text-xs">⟳</span> : <Search className="h-3.5 w-3.5" />}
                            Check
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1 text-muted-foreground"
                            onClick={debugMatchMapping}
                            disabled={matchDebugging || !matchPaId.trim()}
                            title="Run debug resolution steps"
                        >
                            {matchDebugging ? <span className="animate-spin text-xs">⟳</span> : <Bug className="h-3.5 w-3.5" />}
                            Debug
                        </Button>
                    </div>
                </div>

                {/* Current mapping status */}
                {matchMapping && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${
                        matchMapping.statsapi_match_id
                            ? 'border-emerald-500/30 bg-emerald-950/20'
                            : 'border-red-500/30 bg-red-950/20'
                    }`}>
                        {matchMapping.statsapi_match_id ? (
                            <>
                                <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                                <span className="text-emerald-400 font-medium">Mapped:</span>
                                <span className="font-mono text-sm">{matchMapping.statsapi_match_id}</span>
                                {matchMapping.manual && (
                                    <span className="ml-auto text-[10px] text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">manual</span>
                                )}
                            </>
                        ) : (
                            <>
                                <X className="h-3.5 w-3.5 text-red-400 shrink-0" />
                                <span className="text-red-400">No mapping found for PA match {matchMapping.pa_match_id}</span>
                            </>
                        )}
                    </div>
                )}

                {/* StatsAPI Match ID + save/clear */}
                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <p className="text-[10px] text-muted-foreground mb-1">theStatsAPI Match ID</p>
                        <Input
                            placeholder="e.g. mt_642414916"
                            value={matchStatsApiId}
                            onChange={e => setMatchStatsApiId(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && saveMatchMapping()}
                            className="h-8 text-sm font-mono"
                        />
                    </div>
                    <Button
                        size="sm"
                        className="h-8 gap-1"
                        onClick={saveMatchMapping}
                        disabled={matchSaving || !matchPaId.trim() || !matchStatsApiId.trim()}
                    >
                        {matchSaving ? <span className="animate-spin text-xs">⟳</span> : <Check className="h-3.5 w-3.5" />}
                        Save
                    </Button>
                    {matchMapping?.statsapi_match_id && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1 text-destructive hover:text-destructive"
                            onClick={deleteMatchMapping}
                            disabled={matchSaving}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Clear
                        </Button>
                    )}
                </div>

                {/* Debug output */}
                {matchDebugData && (
                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Debug output</p>
                        <pre className="text-[11px] font-mono text-foreground/80 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                            {JSON.stringify(matchDebugData, null, 2)}
                        </pre>
                    </div>
                )}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Search teams..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                />
            </div>

            {loading || loadingTeams ? (
                <div className="space-y-2">
                    {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
            ) : (
                <div className="space-y-5">
                    {/* Unmapped first */}
                    {unmapped.length > 0 && (
                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-red-400">Unmapped ({unmapped.length})</h2>
                            {unmapped.map(t => (
                                <TeamRow
                                    key={t.id}
                                    statsTeam={t}
                                    mapping={null}
                                    onAssign={() => { setEditTeam(t); setPaSearch(''); }}
                                    onRemove={() => deleteMapping(t.id)}
                                />
                            ))}
                        </div>
                    )}

                    {mapped.length > 0 && (
                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-emerald-400">Mapped ({mapped.length})</h2>
                            {mapped.map(t => (
                                <TeamRow
                                    key={t.id}
                                    statsTeam={t}
                                    mapping={mappings[t.id]}
                                    onAssign={() => { setEditTeam(t); setPaSearch(''); }}
                                    onRemove={() => deleteMapping(t.id)}
                                />
                            ))}
                        </div>
                    )}

                    {filtered.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground text-sm">
                            No teams found. Select a competition above.
                        </div>
                    )}
                </div>
            )}

            {/* Assign dialog */}
            <Dialog open={!!editTeam} onOpenChange={open => !open && setEditTeam(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Assign PA team for <span className="text-primary">{editTeam?.name}</span></DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {/* Load from PA competition */}
                        <div className="flex gap-2 items-center p-2.5 rounded-lg bg-muted/40 border border-border/50">
                            <Input
                                placeholder="PA competition ID (e.g. 620)"
                                value={paCompId}
                                onChange={e => setPaCompId(e.target.value)}
                                className="h-8 text-xs"
                                onKeyDown={e => e.key === 'Enter' && loadPaTeamsFromComp()}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                className="shrink-0 h-8 text-xs gap-1"
                                onClick={loadPaTeamsFromComp}
                                disabled={loadingFromPA || !paCompId.trim()}
                            >
                                {loadingFromPA ? <span className="animate-spin">⟳</span> : '↓'}
                                Load
                            </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground -mt-1 px-1">
                            Can't find a team? Enter the PA competition ID above to pull teams from the last 6 months of fixtures.
                        </p>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search PA teams..."
                                value={paSearch}
                                onChange={e => setPaSearch(e.target.value)}
                                className="pl-9"
                                autoFocus
                            />
                        </div>
                        <div className="max-h-72 overflow-y-auto rounded-md border divide-y">
                            {filteredPa.length === 0 ? (
                                <p className="py-4 text-center text-sm text-muted-foreground">No PA teams found</p>
                            ) : filteredPa.map(pa => {
                                const alreadyMapped = paMappings[pa.pa_team_id];
                                const isCurrent = mappings[editTeam?.id]?.pa_team_id === pa.pa_team_id;
                                return (
                                    <button
                                        key={pa.pa_team_id}
                                        disabled={saving}
                                        onClick={() => saveMapping(pa)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent text-left transition-colors disabled:opacity-50"
                                    >
                                        <TeamBadge teamId={pa.pa_team_id} size={24} />
                                        <span className="flex-1 text-sm font-medium">{pa.pa_team_name}</span>
                                        <span className="text-[10px] text-muted-foreground font-mono">{pa.pa_team_id}</span>
                                        {alreadyMapped && !isCurrent && (
                                            <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-400/40">
                                                mapped
                                            </Badge>
                                        )}
                                        {isCurrent && <Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditTeam(null)} disabled={saving}>
                            <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TeamRow({ statsTeam, mapping, onAssign, onRemove }) {
    return (
        <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors
            ${mapping ? 'bg-card border-border' : 'bg-red-950/20 border-red-800/30'}`}>

            {/* StatsAPI team */}
            <div className="w-36 shrink-0">
                <p className="text-sm font-medium leading-tight">{statsTeam.name}</p>
                <p className="text-[10px] text-muted-foreground font-mono">{statsTeam.id}</p>
            </div>

            <span className="text-muted-foreground text-sm">→</span>

            {/* PA team */}
            {mapping ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    <TeamBadge teamId={mapping.pa_team_id} size={28} />
                    <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{mapping.pa_team_name}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{mapping.pa_team_id}</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1">
                    <p className="text-sm text-red-400">Not mapped</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
                <Button
                    variant={mapping ? 'ghost' : 'outline'}
                    size="sm"
                    className={`gap-1 ${!mapping ? 'border-red-500/40 text-red-400 hover:bg-red-950/30' : ''}`}
                    onClick={onAssign}
                >
                    <Pencil className="h-3.5 w-3.5" />
                    {mapping ? 'Change' : 'Assign'}
                </Button>
                {mapping && (
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={onRemove}>
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                )}
            </div>
        </div>
    );
}
